/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { GridBodyLayout } from '@veeam/components';
import { GridCore } from '@veeam/components/lib/GridCore';
import { observer } from 'mobx-react-lite';

import type { GridExpandCollapsePluginConfig } from '@veeam/components/lib/GridCore/plugins/expandCollapse/types/GridExpandCollapsePluginConfig';
import type { GridCoreProps, GetDataByIndexType } from '@veeam/components/lib';

import { GridContext } from './contexts';
import { GridStoreImpl as GridStore } from './stores';
import { useColumns, useContextMenu, useGridStorageActions, useGridUpdates, usePlugins } from './hooks';
import { Pagination } from './components';

import type { GridProps } from './interfaces';

export const Grid = observer(<RowDataT, TotalRowDataT, FilterT>(props: GridProps<RowDataT, TotalRowDataT, FilterT>) => {
    const gridStorageActions = useGridStorageActions<FilterT>('Grid', props.stateId);
    const gridRef = useRef<HTMLDivElement>(null);
    const store = useMemo(() => new GridStore(props, gridStorageActions), []);
    const selectedIds = store.selected.map(item => item[store.selection.field]);

    useImperativeHandle(props.api, () => store); // expose a Grid's API

    const { contextMenu, onContextMenu } = useContextMenu(store, gridRef);

    useEffect(() => {
        store.requestStore.updateFetchMethod(props.data); // sometimes our props.data (= fetchMethod) function is updated, and we need to refresh it in the store
    }, [props.data, store]);

    useGridUpdates(store, props);

    const columns = useColumns(store);

    const idGetter: GridCoreProps<RowDataT>['idGetter'] = useCallback(
        item => item[store.selection.field],
        [store.selection.field]
    );

    const getDataByIndex: GetDataByIndexType<RowDataT> = useCallback(index => store.pageData[index] ?? null, [store.pageData]);

    const getMetaByIndex: GridExpandCollapsePluginConfig<RowDataT>['getMetaByIndex'] = useCallback(index => store.meta[index], [store.meta]);

    const plugins = usePlugins({ props, store, onContextMenu, selectedIds, idGetter, getMetaByIndex });

    const toolbar = useMemo(() => {
        if (!props.toolbars || props.toolbars.length === 0) return;

        return (
            <>
                {props.toolbars.map((Toolbar, index) => <Toolbar key={index} />)}
            </>
        );
    }, [props.toolbars]);

    const footer = useMemo(() => <Pagination />, []);

    const style = useMemo(() => props.style, [JSON.stringify(props.style)]);

    return (
        <GridContext.Provider value={store}>
            <GridBodyLayout
                ref={gridRef}
                fragment={contextMenu}
                toolbar={toolbar}
                grid={(
                    <GridCore
                        getDataByIndex={getDataByIndex}
                        totalItemsCount={store.pageData.length}
                        columns={columns}
                        idGetter={idGetter}
                        plugins={plugins}
                        footer={footer}
                        columnsToggleable={store.columnsStore.toggleable}
                    />
                )}
                style={style}
            />
        </GridContext.Provider>
    );
});
